import React, { useState } from "react"
import {
	ChevronRight,
	Globe,
	BookOpen,
	Target,
	GraduationCap,
} from "lucide-react"
import StreakTracker from "./StreakTracker"
import SkillsChart from "./SkillsChart"
import { useFirebase } from "../context/FirebaseContext"

const HomePage = ({
	streak,
	hasCheckedInToday,
	handleIncreaseStreak,
	handleTabClick,
	handleLanguageChange,
}) => {
	const [showLanguageSelector, setShowLanguageSelector] = useState(false)

	// Get currentLanguage and loading state from Firebase context
	const { currentLanguage, availableLanguages, isLoading } = useFirebase()

	// Handle language selection
	const onSelectLanguage = langCode => {
		if (handleLanguageChange) {
			handleLanguageChange(langCode)
		}
		setShowLanguageSelector(false)
	}

	// Show loading indicator while data is being fetched
	if (isLoading) {
		return (
			<div className='flex justify-center items-center h-64'>
				<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3A6E]'></div>
			</div>
		)
	}

	// Handle the case when currentLanguage is null
	if (!currentLanguage) {
		return (
			<div className='text-center py-10'>
				<div className='text-[#FF3A6E] text-xl mb-4'>Error Loading Data</div>
				<p className='text-[#A0A0A0]'>
					Could not load language data. Please try again later.
				</p>
			</div>
		)
	}

	//const unknownWordsCount = totalFlashcards - knownWordsCount

	return (
		<div className='space-y-6 relative'>
			{/* Greeting Section */}
			<div className='bg-[#1E1E1E] rounded-lg p-5'>
				<div className='flex justify-between items-center mb-2'>
					<h1 className='text-2xl font-bold text-white'>
						Welcome to {currentLanguage.name} Learning!
					</h1>
				</div>
				<p className='text-[#A0A0A0]'>It's a good time to practice.</p>
			</div>

			{/* Daily Streak - Separate Row */}
			<StreakTracker
				streak={streak}
				hasCheckedInToday={hasCheckedInToday}
				handleIncreaseStreak={handleIncreaseStreak}
			/>

			{/* Language Selector */}
			<div className='bg-[#1E1E1E] rounded-lg p-4 relative flex flex-col justify-center'>
				<div
					className='flex items-center justify-between cursor-pointer h-full'
					onClick={() => setShowLanguageSelector(!showLanguageSelector)}>
					<div className='flex items-center'>
						<div className='bg-[#2C2C2C] p-2 rounded-full mr-3 flex-shrink-0'>
							<Globe className='text-[#FF3A6E]' size={20} />
						</div>
						<div>
							<h3 className='text-white font-semibold'>Language</h3>
							<div className='flex items-center'>
								<span className='text-lg mr-1'>{currentLanguage.flag}</span>
								<p className='text-[#A0A0A0] text-sm'>{currentLanguage.name}</p>
							</div>
						</div>
					</div>
					<button className='text-white bg-[#2C2C2C] h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0'>
						<ChevronRight
							size={16}
							className={showLanguageSelector ? "transform rotate-90" : ""}
						/>
					</button>
				</div>

				{/* Language Dropdown */}
				{showLanguageSelector && (
					<div className='absolute left-0 right-0 top-full mt-2 bg-[#2C2C2C] rounded-lg p-2 z-10'>
						{availableLanguages.map(lang => (
							<button
								key={lang.code}
								className={`w-full text-left p-2 rounded-lg mb-1 last:mb-0 flex items-center
                    ${
											currentLanguage.code === lang.code
												? "bg-[#FF3A6E] text-white"
												: "text-white hover:bg-[#3C3C3C]"
										}`}
								onClick={() => onSelectLanguage(lang.code)}>
								<span className='text-lg mr-2'>{lang.flag}</span>
								{lang.name}
							</button>
						))}
					</div>
				)}
			</div>

			<div
				className='bg-gradient-to-r from-[#FF3A6E] to-[#FF6D4B] rounded-lg p-4 md:p-5 cursor-pointer hover:scale-105 transition-transform'
				onClick={() => handleTabClick("learn")}>
				<h2 className='text-white text-lg md:text-xl font-bold mb-2'>
					Start Learning
				</h2>
				<p className='text-white text-opacity-80 text-sm md:text-base mb-3'>
					Jump right into your {currentLanguage.name} learning journey
				</p>
				<button className='bg-white text-[#FF3A6E] py-2 px-4 rounded-lg font-medium text-sm md:text-base hover:bg-gray-100 transition-colors'>
					Go to Learn
				</button>
			</div>

			{/* Learning Statistics */}
			<div>
				<h2 className='text-white text-lg font-bold mb-3'>Your Statistics</h2>

				{/* Statistics Summary Cards */}
				<div className='grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-6'>
					<div className='bg-[#1E1E1E] rounded-lg p-4'>
						<div className='flex items-center mb-2'>
							<Target size={16} className='text-[#FF3A6E] mr-1' />
							<h3 className='text-[#A0A0A0] text-sm'>Current Streak</h3>
						</div>
						<div className='text-white text-xl md:text-2xl font-bold'>
							{streak}
						</div>
						<div className='text-[#FF3A6E] text-xs'>
							{streak > 0
								? `${streak} day${streak > 1 ? "s" : ""}`
								: "Start today!"}
						</div>
					</div>

					<div className='bg-[#1E1E1E] rounded-lg p-4'>
						<div className='flex items-center mb-2'>
							<BookOpen size={16} className='text-blue-400 mr-1' />
							<h3 className='text-[#A0A0A0] text-sm'>Minutes Studied</h3>
						</div>
						<div className='text-white text-xl md:text-2xl font-bold'>45</div>
						<div className='text-blue-400 text-xs'>This week</div>
					</div>

					<div className='bg-[#1E1E1E] rounded-lg p-4'>
						<div className='flex items-center mb-2'>
							<GraduationCap size={16} className='text-green-400 mr-1' />
							<h3 className='text-[#A0A0A0] text-sm'>Lessons Completed</h3>
						</div>
						<div className='text-white text-xl md:text-2xl font-bold'>3</div>
						<div className='text-green-400 text-xs'>Keep going!</div>
					</div>
				</div>

				{/* Skills Distribution Chart */}
				<SkillsChart language={currentLanguage.name} />
			</div>

			{/* Learning Tips */}
			<div className='bg-[#1E1E1E] rounded-lg p-4'>
				<h3 className='text-white font-semibold mb-3'>💡 Learning Tips</h3>
				<ul className='text-[#A0A0A0] text-sm space-y-2'>
					<li className='flex items-start'>
						<span className='text-[#FF3A6E] mr-2'>•</span>
						<span>Practice a little each day to maintain your streak</span>
					</li>
					<li className='flex items-start'>
						<span className='text-[#FF3A6E] mr-2'>•</span>
						<span>Combine different learning methods for better retention</span>
					</li>
					<li className='flex items-start'>
						<span className='text-[#FF3A6E] mr-2'>•</span>
						<span>
							Review what you've learned regularly to reinforce your memory
						</span>
					</li>
					<li className='flex items-start'>
						<span className='text-[#FF3A6E] mr-2'>•</span>
						<span>Set aside dedicated time each day for language learning</span>
					</li>
				</ul>
			</div>
		</div>
	)
}

export default HomePage
